import { skipToken, useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ListArInvoicesInput,
  ListArInvoicesResponse,
  GetArInvoiceResponse,
  ArInvoiceListItem,
} from '@erp/rpc';
// AR Vision Demo (GEN-2247) — demo-mode shim. Delete with frontend/src/demo/ar.
import {
  arDemoActive,
  useArDemoFixture,
  withArDemoData,
} from '@/demo/ar/hook-shim';

export function useArInvoicesList(filters: ListArInvoicesInput = {}) {
  const demoData = useArDemoFixture(
    (s): ListArInvoicesResponse => ({
      invoices: s.invoiceList,
      pagination: {
        total: s.invoiceList.length,
        limit: s.invoiceList.length || 1,
        offset: 0,
      },
    }),
  );
  const baseQuery = useQuery({
    ...orpc.accountsReceivable.arInvoices.list.queryOptions({ input: filters }),
    enabled: !arDemoActive(),
  });
  const query = withArDemoData(baseQuery, demoData);

  const invoices = query.data?.invoices ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    invoices,
    pagination,
  } satisfies typeof query & {
    data?: ListArInvoicesResponse;
    invoices: ArInvoiceListItem[];
    pagination: ListArInvoicesResponse['pagination'] | undefined;
  };
}

export function useArInvoice(id: string | undefined, enabled = true) {
  const demoData = useArDemoFixture(
    (s): GetArInvoiceResponse => ({
      invoice: (id && s.invoicesById[id]) || null,
    }),
  );
  const baseQuery = useQuery({
    ...orpc.accountsReceivable.arInvoices.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled && !arDemoActive(),
  });
  const query = withArDemoData(baseQuery, demoData);

  return {
    ...query,
    invoice: query.data?.invoice ?? null,
  } satisfies typeof query & {
    data?: GetArInvoiceResponse;
    invoice: GetArInvoiceResponse['invoice'] | null;
  };
}
